import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { ColorPicker } from "@/components/ui/color-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useWizardStore } from "@/store/webinar/wizardStore";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Upload, Eye } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { nanoid } from "nanoid";
import { InscricaoPreview } from "@/components/webinar/inscricao/InscricaoPreview";
import { useWizardTelemetry } from "@/hooks/useWizardTelemetry";

function SortableFormField({ field, index, onUpdate, onRemove }: any) {
  useLanguage();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-3 p-4 border rounded-lg bg-card">
      <button {...attributes} {...listeners} className="mt-2 cursor-grab active:cursor-grabbing">
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      
      <div className="flex-1 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{field.name === 'name' ? translateInterfaceText("Nome") : field.name === 'email' ? translateInterfaceText("E-mail") : field.name === 'whatsapp' ? translateInterfaceText("WhatsApp") : translateInterfaceText("Campo Extra")}</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={field.enabled}
              onCheckedChange={(checked) => onUpdate(index, { ...field, enabled: checked })}
            />
            <span className="text-xs text-muted-foreground">{translateInterfaceText("Ativo")}</span>
          </div>
        </div>
        
        <Input
          value={field.label}
          onChange={(e) => onUpdate(index, { ...field, label: e.target.value })}
          placeholder={translateInterfaceText("Rótulo do campo")}
        />
        
        <Input
          value={field.placeholder}
          onChange={(e) => onUpdate(index, { ...field, placeholder: e.target.value })}
          placeholder={translateInterfaceText("Placeholder")}
        />
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={field.required}
              onCheckedChange={(checked) => onUpdate(index, { ...field, required: checked })}
            />
            <span className="text-xs text-muted-foreground">{translateInterfaceText("Obrigatório")}</span>
          </div>
          
          {field.name !== 'name' && field.name !== 'email' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function InscricaoStep() {
  useLanguage();
  const { data, updateNestedData } = useWizardStore();
  const config = data.login_config;
  const { trackEvent } = useWizardTelemetry();
  const [previewOpen, setPreviewOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = config.form_fields.findIndex((f: any) => f.id === active.id);
      const newIndex = config.form_fields.findIndex((f: any) => f.id === over.id);
      const newFields = arrayMove(config.form_fields, oldIndex, newIndex);
      updateNestedData('login_config', 'form_fields', newFields);
    }
  };

  const updateFormField = (index: number, updatedField: any) => {
    const newFields = [...config.form_fields];
    newFields[index] = updatedField;
    updateNestedData('login_config', 'form_fields', newFields);
    trackEvent('inscricao.change', { field: 'form_fields', index });
  };

  const removeFormField = (index: number) => {
    const newFields = config.form_fields.filter((_: any, i: number) => i !== index);
    updateNestedData('login_config', 'form_fields', newFields);
  };

  const addExtraField = () => {
    const newField = {
      id: nanoid(),
      name: `extra_${config.form_fields.length + 1}`,
      label: 'Campo Personalizado',
      type: 'text' as const,
      required: false,
      placeholder: 'Digite aqui',
      enabled: true
    };
    updateNestedData('login_config', 'form_fields', [...config.form_fields, newField]);
  };

  const updateTrustSeal = (index: number, updates: any) => {
    const newSeals = [...config.trust_seals];
    newSeals[index] = { ...newSeals[index], ...updates };
    updateNestedData('login_config', 'trust_seals', newSeals);
  };

  const updateFooterLink = (index: number, updates: any) => {
    const newLinks = [...config.footer_links];
    newLinks[index] = { ...newLinks[index], ...updates };
    updateNestedData('login_config', 'footer_links', newLinks);
  };

  const addFooterLink = () => {
    const newLink = {
      id: nanoid(),
      label: 'Novo Link',
      url: '#'
    };
    updateNestedData('login_config', 'footer_links', [...config.footer_links, newLink]);
  };

  const removeFooterLink = (index: number) => {
    const newLinks = config.footer_links.filter((_: any, i: number) => i !== index);
    updateNestedData('login_config', 'footer_links', newLinks);
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertDescription>
          {translateInterfaceText("Configure a página de inscrição do seu webinário. As alterações aparecem na prévia em tempo real.")}</AlertDescription>
      </Alert>

      {/* Mobile/Tablet Preview Button */}
      <div className="xl:hidden">
        <Dialog open={previewOpen} onOpenChange={(open) => {
          setPreviewOpen(open);
          if (open) trackEvent('ui.preview.opened', { device: 'mobile' });
        }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              <Eye className="h-4 w-4 mr-2" />
              {translateInterfaceText("Ver Prévia")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-[520px] max-h-[90vh] overflow-y-auto shadow-[var(--wizard-shadow-modal)]">
            <DialogHeader>
              <DialogTitle>{translateInterfaceText("Prévia da Página de Inscrição")}</DialogTitle>
            </DialogHeader>
            <InscricaoPreview config={config} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Desktop: Grid 60/40 */}
      <div className="xl:grid xl:grid-cols-[60%_40%] gap-8">
        {/* Left Column - Configuration Panel */}
        <div className="space-y-6" id="inscricao-panel">
          {/* Identidade Visual */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Identidade Visual")}</CardTitle>
              <CardDescription>{translateInterfaceText("Personalize a aparência da página de inscrição")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{translateInterfaceText("Logo do Webinário")}</Label>
                <div className="mt-2 flex items-center gap-4">
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Enviar Logo (220x60)")}</Button>
                  {config.logo_url && (
                    <img src={config.logo_url} alt={translateInterfaceText("Logo")} className="h-10 object-contain" />
                  )}
                </div>
              </div>

              <div>
                <Label>{translateInterfaceText("Alinhamento do Logo")}</Label>
                <RadioGroup
                  value={config.logo_alignment}
                  onValueChange={(value: any) => updateNestedData('login_config', 'logo_alignment', value)}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="left" id="left" />
                    <Label htmlFor="left" className="font-normal">{translateInterfaceText("Esquerda")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="center" id="center" />
                    <Label htmlFor="center" className="font-normal">{translateInterfaceText("Centro")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="right" id="right" />
                    <Label htmlFor="right" className="font-normal">{translateInterfaceText("Direita")}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{translateInterfaceText("Cor de Fundo do Card")}</Label>
                <div className="mt-2">
                  <ColorPicker
                    color={config.background_color || '#ffffff'}
                    onChange={(color) => updateNestedData('login_config', 'background_color', color)}
                  />
                </div>
              </div>

              <div>
                <Label>{translateInterfaceText("Imagem de Fundo (Opcional)")}</Label>
                <div className="mt-2 flex items-center gap-4">
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Enviar Imagem")}</Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={config.background_blur}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'background_blur', checked)}
                />
                <Label>{translateInterfaceText("Aplicar desfoque na imagem de fundo (blur 8px)")}</Label>
              </div>
            </CardContent>
          </Card>

          {/* Indicador de Disponibilidade */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Indicador de Disponibilidade")}</CardTitle>
              <CardDescription>{translateInterfaceText("Mostre vagas disponíveis ou progresso")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{translateInterfaceText("Tipo de Indicador")}</Label>
                <RadioGroup
                  value={config.availability_type}
                  onValueChange={(value: any) => updateNestedData('login_config', 'availability_type', value)}
                  className="flex flex-col gap-2 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="none" id="none" />
                    <Label htmlFor="none" className="font-normal">{translateInterfaceText("Nenhum")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="line" id="line" />
                    <Label htmlFor="line" className="font-normal">{translateInterfaceText("Linha Fina")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="segmented" id="segmented" />
                    <Label htmlFor="segmented" className="font-normal">{translateInterfaceText("Segmentado (Pills)")}</Label>
                  </div>
                </RadioGroup>
              </div>

              {config.availability_type !== 'none' && (
                <>
                  {config.availability_type === 'line' && (
                    <div>
                      <Label>{translateInterfaceText("Espessura da Linha (1-6px)")}</Label>
                      <Slider
                        value={[config.availability_thickness]}
                        onValueChange={(value) => updateNestedData('login_config', 'availability_thickness', value[0])}
                        min={1}
                        max={6}
                        step={1}
                        className="mt-2"
                      />
                      <span className="text-sm text-muted-foreground">{config.availability_thickness}px</span>
                    </div>
                  )}

                  <div>
                    <Label>{translateInterfaceText("Cor Base")}</Label>
                    <div className="mt-2">
                      <ColorPicker
                        color={config.availability_color_base}
                        onChange={(color) => updateNestedData('login_config', 'availability_color_base', color)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{translateInterfaceText("Cor do Progresso")}</Label>
                    <div className="mt-2">
                      <ColorPicker
                        color={config.availability_color_progress}
                        onChange={(color) => updateNestedData('login_config', 'availability_color_progress', color)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{translateInterfaceText("Progresso Inicial (0-100%)")}</Label>
                    <Slider
                      value={[config.availability_initial]}
                      onValueChange={(value) => updateNestedData('login_config', 'availability_initial', value[0])}
                      min={0}
                      max={100}
                      step={1}
                      className="mt-2"
                    />
                    <span className="text-sm text-muted-foreground">{config.availability_initial}%</span>
                  </div>

                  <div>
                    <Label>{translateInterfaceText("Texto acima do indicador (opcional, máx 40 caracteres)")}</Label>
                    <Input
                      value={config.availability_label || ''}
                      onChange={(e) => updateNestedData('login_config', 'availability_label', e.target.value)}
                      placeholder={translateInterfaceText("Ex: Restam poucas vagas")}
                      maxLength={40}
                    />
                    <span className="text-xs text-muted-foreground">{(config.availability_label || '').length}/40</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Título da Página */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Título da Página")}</CardTitle>
              <CardDescription>{translateInterfaceText("Atraia atenção com um título impactante")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{translateInterfaceText("Título Principal (máx 150 caracteres)")}</Label>
                <Textarea
                  value={config.page_title}
                  onChange={(e) => updateNestedData('login_config', 'page_title', e.target.value)}
                  placeholder={translateInterfaceText("[AO VIVO] Crie seu webinário do zero em 60 minutos")}
                  maxLength={150}
                  rows={2}
                  className="mt-2"
                />
                <span className="text-xs text-muted-foreground">{config.page_title.length}/150</span>
              </div>

              <div>
                <Label>{translateInterfaceText("Subtítulo (máx 160 caracteres)")}</Label>
                <Textarea
                  value={config.page_subtitle}
                  onChange={(e) => updateNestedData('login_config', 'page_subtitle', e.target.value)}
                  placeholder={translateInterfaceText("Treinamento prático para criar, configurar e abrir sua sala")}
                  maxLength={160}
                  rows={2}
                  className="mt-2"
                />
                <span className="text-xs text-muted-foreground">{config.page_subtitle.length}/160</span>
              </div>
            </CardContent>
          </Card>

          {/* Chamada Principal (CTA) */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Chamada Principal (CTA)")}</CardTitle>
              <CardDescription>{translateInterfaceText("Configure o botão de inscrição")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{translateInterfaceText("Texto do Botão (máx 40 caracteres)")}</Label>
                <Input
                  value={config.cta_label}
                  onChange={(e) => updateNestedData('login_config', 'cta_label', e.target.value)}
                  placeholder={translateInterfaceText("Entrar na Sala")}
                  maxLength={40}
                  className="mt-2"
                />
                <span className="text-xs text-muted-foreground">{config.cta_label.length}/40</span>
              </div>

              <div>
                <Label>{translateInterfaceText("Estilo do Botão")}</Label>
                <RadioGroup
                  value={config.cta_style}
                  onValueChange={(value: any) => updateNestedData('login_config', 'cta_style', value)}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="solid" id="solid" />
                    <Label htmlFor="solid" className="font-normal">{translateInterfaceText("Sólido")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="outline" id="outline" />
                    <Label htmlFor="outline" className="font-normal">{translateInterfaceText("Contornado")}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{translateInterfaceText("Comportamento")}</Label>
                <RadioGroup
                  value={config.cta_behavior}
                  onValueChange={(value: any) => updateNestedData('login_config', 'cta_behavior', value)}
                  className="flex flex-col gap-2 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="submit_enter" id="submit_enter" />
                    <Label htmlFor="submit_enter" className="font-normal">{translateInterfaceText("Enviar e entrar na sala")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="submit_thankyou" id="submit_thankyou" />
                    <Label htmlFor="submit_thankyou" className="font-normal">{translateInterfaceText("Enviar e ir para página de obrigado")}</Label>
                  </div>
                </RadioGroup>
              </div>

              {config.cta_behavior === 'submit_thankyou' && (
                <div>
                  <Label>{translateInterfaceText("URL da Página de Obrigado")}</Label>
                  <Input
                    value={config.cta_thankyou_url || ''}
                    onChange={(e) => updateNestedData('login_config', 'cta_thankyou_url', e.target.value)}
                    placeholder="https://exemplo.com/obrigado"
                    type="url"
                    className="mt-2"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Campos do Formulário */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Campos do Formulário")}</CardTitle>
              <CardDescription>{translateInterfaceText("Organize e personalize os campos de inscrição (arraste para reordenar)")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={config.form_fields.map((f: any) => f.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {config.form_fields.map((field: any, index: number) => (
                      <SortableFormField
                        key={field.id}
                        field={field}
                        index={index}
                        onUpdate={updateFormField}
                        onRemove={removeFormField}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <Button variant="outline" onClick={addExtraField} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                {translateInterfaceText("Adicionar Campo Extra")}</Button>
            </CardContent>
          </Card>

          {/* LGPD/Consentimento */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Consentimento LGPD")}</CardTitle>
              <CardDescription>{translateInterfaceText("Configure o termo de consentimento")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.lgpd_enabled}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'lgpd_enabled', checked)}
                />
                <Label>{translateInterfaceText("Exibir termo de consentimento")}</Label>
              </div>

              {config.lgpd_enabled && (
                <>
                  <div>
                    <Label>{translateInterfaceText("Texto do Termo (máx 250 caracteres)")}</Label>
                    <Textarea
                      value={config.lgpd_text}
                      onChange={(e) => updateNestedData('login_config', 'lgpd_text', e.target.value)}
                      placeholder={translateInterfaceText("Ao me inscrever, concordo em receber comunicações e aceito a política de privacidade.")}
                      maxLength={250}
                      rows={3}
                      className="mt-2"
                    />
                    <span className="text-xs text-muted-foreground">{config.lgpd_text.length}/250</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config.lgpd_required}
                      onCheckedChange={(checked) => updateNestedData('login_config', 'lgpd_required', checked)}
                    />
                    <Label>{translateInterfaceText("Tornar obrigatório")}</Label>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Selos de Confiança */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Selos de Confiança")}</CardTitle>
              <CardDescription>{translateInterfaceText("Mostre elementos que transmitam segurança")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.trust_seals_enabled}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'trust_seals_enabled', checked)}
                />
                <Label>{translateInterfaceText("Exibir selos de segurança")}</Label>
              </div>

              {config.trust_seals_enabled && (
                <div className="space-y-3">
                  {config.trust_seals.map((seal: any, index: number) => (
                    <div key={seal.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Switch
                        checked={seal.enabled}
                        onCheckedChange={(checked) => updateTrustSeal(index, { enabled: checked })}
                      />
                      <Input
                        value={seal.label}
                        onChange={(e) => updateTrustSeal(index, { label: e.target.value })}
                        placeholder={translateInterfaceText("Ex: SSL Seguro")}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Links do Rodapé */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Links do Rodapé")}</CardTitle>
              <CardDescription>{translateInterfaceText("Adicione links de termos, privacidade, etc.")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.footer_links.map((link: any, index: number) => (
                <div key={link.id} className="flex items-center gap-2">
                  <Input
                    value={link.label}
                    onChange={(e) => updateFooterLink(index, { label: e.target.value })}
                    placeholder={translateInterfaceText("Texto do Link")}
                    className="flex-1"
                  />
                  <Input
                    value={link.url}
                    onChange={(e) => updateFooterLink(index, { url: e.target.value })}
                    placeholder={translateInterfaceText("URL")}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFooterLink(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}

              <Button variant="outline" onClick={addFooterLink} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                {translateInterfaceText("Adicionar Link")}</Button>
            </CardContent>
          </Card>

          {/* Aprimoradores de Conversão */}
          <Card className="rounded-[var(--wizard-card-radius)]">
            <CardHeader>
              <CardTitle>{translateInterfaceText("Aprimoradores de Conversão")}</CardTitle>
              <CardDescription>{translateInterfaceText("Elementos extras para aumentar conversão")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.countdown_enabled}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'countdown_enabled', checked)}
                />
                <Label>{translateInterfaceText("Mostrar contagem regressiva")}</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={config.testimonial_enabled}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'testimonial_enabled', checked)}
                />
                <Label>{translateInterfaceText("Exibir depoimento")}</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={config.timezone_indicator}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'timezone_indicator', checked)}
                />
                <Label>{translateInterfaceText("Indicador de fuso horário")}</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={config.event_badge_enabled}
                  onCheckedChange={(checked) => updateNestedData('login_config', 'event_badge_enabled', checked)}
                />
                <Label>{translateInterfaceText("Badge \"Sessão atualizada hoje\"")}</Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Sticky Preview (Desktop only) */}
        <div className="hidden xl:block">
          <div className="sticky top-[88px]" id="inscricao-preview">
            <InscricaoPreview config={config} />
          </div>
        </div>
      </div>
    </div>
  );
}
